import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDSButton, IDSAlert, IDSRadio, IDSRadioGroup, IDSCard, IDSTextarea } from "@inera/ids-react";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  setReviewedByUserId,
  setTestExecutionStatus,
  startDeviationHandling,
  downloadDecisionDocument,
  generateDecisionDocument,
  uploadDecisionDocumentToServiceNow,
} from "../services/testExecutionAPI";
import useReviewReport from "../components/testreports/review/useReviewReport";
import useSubmissionReview from "../components/testreports/review/useSubmissionReview";
import {
  SubmissionHeader,
  SubmissionMeta,
  SubmissionTestResults,
  SubmissionDeviations,
  SubmissionAnswers,
} from "../components/testreports/review/SubmissionReviewShared";
import DeviationReviewCard from "../components/testreports/DeviationReviewCard";
import { formatDateTimeNoSeconds } from "../utils/dateTime";

/** Statuses a report can only reach once a reviewer has started reviewing it. */
const REVIEW_STARTED_STATUSES = new Set(["IN_REVIEW", "IN_CLARIFY", "IN_DEVIATION"]);

/**
 * Reviewer's view of a submitted self-declaration: read-only answers/results
 * with the "Avviker"-filter, plus reviewer actions – request clarification,
 * deviation handling and the final decision.
 */
const ReviewerReview = () => {
  const navigate = useNavigate();
  const { report, setReport, loading } = useReviewReport();

  const review = useSubmissionReview(report);

  const [isStartingReview, setIsStartingReview] = useState(false);
  const [startReviewError, setStartReviewError] = useState(null);
  const [showDeviationCard, setShowDeviationCard] = useState(false);
  const [selectedTestResultIds, setSelectedTestResultIds] = useState(new Set());
  const [isStartingDeviation, setIsStartingDeviation] = useState(false);
  const [deviationError, setDeviationError] = useState(null);
  const [showDecision, setShowDecision] = useState(false);
  const [decision, setDecision] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [clarifyError, setClarifyError] = useState(null);
  const [isRequestingClarification, setIsRequestingClarification] = useState(false);
  const [showClarifyConfirm, setShowClarifyConfirm] = useState(false);
  const [completeOrderError, setCompleteOrderError] = useState(null);
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [isGeneratingDecision, setIsGeneratingDecision] = useState(false);
  const [isUploadingDecision, setIsUploadingDecision] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [navSections, setNavSections] = useState([]);

  if (loading) {
    return (
      <div style={{ maxWidth: 950, margin: "2rem auto", padding: "0 1rem" }}>
        <p>Laddar rapport...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={{ maxWidth: 950, margin: "2rem auto", padding: "0 1rem" }}>
        <IDSAlert type="attention"><p>Ingen rapport vald. Öppna granskningen från Testrapporter.</p></IDSAlert>
        <IDSButton className="ids-mt-4" secondary onClick={() => navigate("/testrapporter")}>Till testrapporter</IDSButton>
      </div>
    );
  }

  const { sections, groupedTests, reviewScenario, badge, unexpectedCount, failedResults, commentsByQuestion, addComment, removeComment } = review;
  const refreshAndLeave = () => navigate("/testrapporter");

  // Keeps report.testResults in sync when a test-result comment is added/edited/removed —
  // otherwise "flaggedUnresponded" etc. only pick it up after a full page reload.
  const handleTestResultCommentsChanged = (testResultId, comments) => {
    setReport((prev) => ({
      ...prev,
      testResults: (prev.testResults || []).map((tr) =>
        tr.id === testResultId ? { ...tr, comments } : tr
      ),
    }));
  };

  // Reviewer may comment while actively reviewing.
  const canAddComments = report.status === "IN_REVIEW";

  // Assign the current reviewer and move the report into IN_REVIEW, then reflect
  // it in place so comments and the reviewer actions unlock without navigating.
  const handleStartReview = () => {
    setIsStartingReview(true);
    setStartReviewError(null);
    let assigned;
    setReviewedByUserId(report.id)
      .then((res) => {
        assigned = res;
        return setTestExecutionStatus(report.id, "IN_REVIEW");
      })
      .then(() => {
        setReport((prev) => ({
          ...prev,
          status: "IN_REVIEW",
          reviewedByUserEmail: assigned?.reviewedByUserEmail ?? prev.reviewedByUserEmail,
        }));
      })
      .catch((err) => {
        console.error("Error starting review:", err);
        setStartReviewError("Kunde inte påbörja granskningen. Försök igen.");
      })
      .finally(() => setIsStartingReview(false));
  };

  // Each request comments on the ServiceNow case, so the customer's contact person hears about
  // it. Disabled while in flight: a double-click would ask twice and notify them twice.
  const handleRequestClarification = () => {
    setClarifyError(null);
    setIsRequestingClarification(true);
    setTestExecutionStatus(report.id, "IN_CLARIFY")
      .then(refreshAndLeave)
      .catch((e) => {
        console.error(e);
        setClarifyError("Kunde inte begära förtydligande. Försök igen.");
        setIsRequestingClarification(false);
      });
  };

  const openClarifyConfirmation = () => {
    setClarifyError(null);
    setShowClarifyConfirm(true);
  };

  const handleOpenDeviationCard = () => {
    if (showDeviationCard) {
      setShowDeviationCard(false);
      setDeviationError(null);
    } else {
      setSelectedTestResultIds(new Set(failedResults.map((r) => r.id)));
      setDeviationError(null);
      setShowDeviationCard(true);
    }
  };

  const handleToggleDecision = () => {
    setShowDecision((prev) => {
      const next = !prev;
      if (next && selectedTestResultIds.size === 0) {
        setSelectedTestResultIds(new Set(failedResults.map((r) => r.id)));
      }
      return next;
    });
  };

  const handleToggleDeviation = (id, shouldSend) => {
    setSelectedTestResultIds((prev) => {
      const next = new Set(prev);
      if (shouldSend) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleConfirmDeviations = (
    generalExceptionKeys = {},
    similarTicketKeys = {},
    overrideTkbRules = undefined,
    sharedBodyNote = '',
    perTicketBodyNotes = {}
  ) => {
    if (selectedTestResultIds.size === 0) return;
    setIsStartingDeviation(true);
    setDeviationError(null);
    startDeviationHandling(
      report.id,
      Array.from(selectedTestResultIds),
      generalExceptionKeys,
      similarTicketKeys,
      overrideTkbRules,
      sharedBodyNote,
      perTicketBodyNotes
    )
      .then(refreshAndLeave)
      .catch((err) => {
        console.error("Error starting deviation handling:", err);
        setIsStartingDeviation(false);
        const status = err && err.status;
        const isTransient = !status || status === 408 || status === 429 || status >= 500;
        setDeviationError(isTransient ? "Det gick inte att skapa Jira-ärende. Försök igen." : "Det gick inte att starta avvikelsehantering. Kontakta administratör.");
      });
  };

  // The comment is stored with the decision and printed in the decision document the backend
  // renders once the review is closed.
  const handleCompleteOrder = (e) => {
    e.preventDefault();
    if (!decision || isCompletingOrder) return;
    setIsCompletingOrder(true);
    setCompleteOrderError(null);
    setTestExecutionStatus(report.id, decision, decisionComment)
      .then(refreshAndLeave)
      .catch((err) => {
        console.error("Error completing order:", err);
        setIsCompletingOrder(false);
        setCompleteOrderError("Kunde inte slutföra ärendet. Försök igen.");
      });
  };

  const handleDownloadDecision = () => {
    setDownloadError(null);
    downloadDecisionDocument(report.id).catch((err) => {
      console.error("Error downloading decision document:", err);
      setDownloadError(err.message || "Kunde inte hämta beslutsunderlaget.");
    });
  };

  // A decided review can lack a document — it was decided before decision documents existed, or
  // rendering failed after the decision was committed. Building it is safe to repeat.
  const handleGenerateDecision = () => {
    setDownloadError(null);
    setIsGeneratingDecision(true);
    generateDecisionDocument(report.id)
      .then((updated) => {
        setReport((prev) => ({ ...prev, hasDecisionDocument: updated?.hasDecisionDocument ?? true }));
      })
      .catch((err) => {
        console.error("Error generating decision document:", err);
        setDownloadError("Kunde inte skapa beslutsunderlaget. Försök igen.");
      })
      .finally(() => setIsGeneratingDecision(false));
  };

  // The backend attaches the document to the ServiceNow case as soon as it is rendered; this is
  // the retry for when that did not go through — ServiceNow was down, or the order lacked a case.
  const handleUploadDecisionToServiceNow = () => {
    setUploadError(null);
    setIsUploadingDecision(true);
    uploadDecisionDocumentToServiceNow(report.id)
      .then((updated) => {
        setReport((prev) => ({
          ...prev,
          decisionDocumentCaseNumber: updated?.decisionDocumentCaseNumber ?? null,
          decisionDocumentUploadedAt: updated?.decisionDocumentUploadedAt ?? null,
        }));
      })
      .catch((err) => {
        console.error("Error uploading decision document to ServiceNow:", err);
        setUploadError(
          err.status === 404
            ? "Granskningen saknar en beställning med ServiceNow-ärendenummer."
            : "Kunde inte bifoga beslutsunderlaget till ServiceNow-ärendet. Försök igen."
        );
      })
      .finally(() => setIsUploadingDecision(false));
  };

  const isFinished = report.status === "APPROVED" || report.status === "REJECTED";
  // A started review is recognised from the status, not from the reviewer's email: the email is
  // resolved by the listing/read endpoints, so gating on it left the actions hidden until a
  // reload right after "Påbörja granskning".
  const reviewStarted =
    REVIEW_STARTED_STATUSES.has(report.status) || !!report.reviewedByUserEmail;
  // While IN_CLARIFY the ball is in the tester's court — the reviewer has nothing to decide
  // until a clarified submission comes back, so the decision actions stay hidden.
  const showActions = reviewStarted && !isFinished && report.status !== "IN_CLARIFY";
  const shouldShowDeviationCard = showDeviationCard;
  // Before review is started the report is read-only; the reviewer kicks it off here.
  const reviewNotStarted = !reviewStarted && !isFinished;

  return (
    <div style={{ maxWidth: 1200, margin: "2rem auto", padding: "0 1rem", display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
      <SubmissionHeader report={report} badge={badge} breadcrumbLabel="Testrapporter" title={"Granska självdeklaration"} showReviewerContact />
      <SubmissionMeta report={report} reviewScenario={reviewScenario} />

      {reviewNotStarted && (
        <div className="ids-mt-4 ids-mb-4">
          <IDSButton size="m" onClick={handleStartReview} disabled={isStartingReview}>
            {isStartingReview ? "Påbörjar granskning..." : "Påbörja granskning"}
          </IDSButton>
          {startReviewError && (
            <div className="ids-mt-2">
              <IDSAlert type="error"><p>{startReviewError}</p></IDSAlert>
            </div>
          )}
        </div>
      )}

      <SubmissionTestResults report={report} groupedTests={groupedTests} userRole="REVIEWER" canAddComments={canAddComments}
        onTestResultCommentsChanged={handleTestResultCommentsChanged} />

      <SubmissionAnswers
        report={report}
        sections={sections}
        commentsByQuestion={commentsByQuestion}
        isReviewer={true}
        showUnexpectedFilter={true}
        unexpectedCount={unexpectedCount}
        canAddComments={canAddComments}
        reviewScenario={reviewScenario}
        onAdded={addComment}
        onDeleted={removeComment}
      />

      {/* Decision (read-only for finished reports) */}
      {isFinished && (
        <IDSCard className="ids-mt-6">
          <h2 className="ids-heading-m">Beslut</h2>
          <p><strong>Beslut:</strong> {report.status === "APPROVED" ? "Godkänd" : "Underkänd"}</p>
          {report.decisionComment && (
            <p style={{ whiteSpace: "pre-wrap" }}>
              <strong>Kommentar:</strong> {report.decisionComment}
            </p>
          )}
          {report.hasDecisionDocument ? (
            <div className="ids-mt-2">
              <IDSButton size="s" secondary onClick={handleDownloadDecision}>
                Hämta beslutsunderlag (PDF)
              </IDSButton>
              {report.decisionDocumentCaseNumber ? (
                <div style={{ fontSize: "12px", color: "var(--ids-palette-neutral-50)", marginTop: "6px" }}>
                  Bifogat till ServiceNow-ärende {report.decisionDocumentCaseNumber}
                  {report.decisionDocumentUploadedAt
                    ? ` ${formatDateTimeNoSeconds(report.decisionDocumentUploadedAt)}`
                    : ""}
                  .
                </div>
              ) : (
                <div className="ids-mt-2">
                  <IDSButton
                    size="s"
                    secondary
                    onClick={handleUploadDecisionToServiceNow}
                    disabled={isUploadingDecision}
                  >
                    {isUploadingDecision ? "Skickar till ServiceNow..." : "Bifoga i ServiceNow-ärendet"}
                  </IDSButton>
                  <div style={{ fontSize: "12px", color: "var(--ids-palette-neutral-50)", marginTop: "6px" }}>
                    Beslutsunderlaget är inte bifogat i ServiceNow. Det bifogas normalt automatiskt när ärendet slutförs.
                  </div>
                </div>
              )}
              {uploadError && (
                <div className="ids-mt-2">
                  <IDSAlert type="error"><p>{uploadError}</p></IDSAlert>
                </div>
              )}
            </div>
          ) : (
            <div className="ids-mt-2">
              <IDSButton size="s" secondary onClick={handleGenerateDecision} disabled={isGeneratingDecision}>
                {isGeneratingDecision ? "Skapar beslutsunderlag..." : "Skapa beslutsunderlag (PDF)"}
              </IDSButton>
              <div style={{ fontSize: "12px", color: "var(--ids-palette-neutral-50)", marginTop: "6px" }}>
                Det här beslutet saknar beslutsunderlag. Det skapas normalt automatiskt när ärendet slutförs.
              </div>
            </div>
          )}
          {downloadError && (
            <div className="ids-mt-2">
              <IDSAlert type="error"><p>{downloadError}</p></IDSAlert>
            </div>
          )}
        </IDSCard>
      )}

      {/* Reviewer actions*/}
      {showActions && (
        <section style={{ marginTop: "2rem" }}>
          <div className="ids-flex" style={{ gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
               <IDSButton size="m" onClick={openClarifyConfirmation} disabled={isRequestingClarification}>
                  {isRequestingClarification ? "Begär förtydligande..." : "Begär förtydligande"}
                </IDSButton>
                {report.status !== "IN_DEVIATION" && (
                  <IDSButton size="m" onClick={handleOpenDeviationCard} disabled={isStartingDeviation}>
                    {showDeviationCard ? "Stäng avvikelser" : "Påbörja avvikelsehantering"}
                  </IDSButton>
                )}
                <IDSButton size="m" onClick={handleToggleDecision}>Slutför ärende</IDSButton>
          </div>
          {clarifyError && (
            <div className="ids-mt-2">
              <IDSAlert type="error"><p>{clarifyError}</p></IDSAlert>
            </div>
          )}

          <ConfirmDialog
            isOpen={showClarifyConfirm}
            title="Är du säker?"
            message="Har du lagt till kommentarer och vill begära förtydligande från användaren? Detta skickar ärendet tillbaka för komplettering."
            confirmLabel="Ja"
            cancelLabel="Nej"
            onConfirm={() => {
              setShowClarifyConfirm(false);
              handleRequestClarification();
            }}
            onCancel={() => setShowClarifyConfirm(false)}
          />

          {shouldShowDeviationCard && (
            <div className="ids-mt-4">
              <DeviationReviewCard failedResults={failedResults} selectedIds={selectedTestResultIds}
                onToggle={handleToggleDeviation} onConfirm={handleConfirmDeviations}
                onCancel={() => { setShowDeviationCard(false); setDeviationError(null); }}
                isSubmitting={isStartingDeviation} error={deviationError} executionId={report.id} />
            </div>
          )}

          {showDecision && (
            <IDSCard className="ids-mt-4">
              <SubmissionDeviations deviations={report.deviations || []} testResults={report.testResults || []} showJiraLink />
              <form onSubmit={handleCompleteOrder}>
                <h3 className="ids-heading-s ids-mb-2">Registrera beslut</h3>
                <div className="ids-mb-4">
                  <IDSRadioGroup legend="Beslut" name={`decision-${report.id}`} onRadioChange={(e) => setDecision(e.target.value)}>
                    <IDSRadio value="APPROVED" defaultChecked={decision === "APPROVED"}>Godkänd</IDSRadio>
                    <IDSRadio value="REJECTED" defaultChecked={decision === "REJECTED"}>Underkänd</IDSRadio>
                  </IDSRadioGroup>
                </div>
                <div className="ids-mb-4">
                  <IDSTextarea
                    id={`decision-comment-${report.id}`}
                    label="Kommentar"
                    rows={4}
                    maxLength={4000}
                    placeholder="Motivera beslutet. Kommentaren sparas med beslutet och skrivs ut i beslutsunderlaget."
                    value={decisionComment}
                    onInput={(e) => setDecisionComment(e.target.value)}
                    onChange={(e) => setDecisionComment(e.target.value)}
                    style={{ width: "100%", resize: "vertical" }}
                  />
                  <div style={{ fontSize: "12px", color: "var(--ids-palette-neutral-50)", marginTop: "6px" }}>
                    Ett beslutsunderlag i PDF skapas automatiskt när ärendet slutförs.
                  </div>
                </div>
                <IDSButton size="m" type="submit" disabled={isCompletingOrder}>
                  {isCompletingOrder ? "Slutför..." : "Slutför"}
                </IDSButton>
                {completeOrderError && (
                  <div className="ids-mt-2">
                    <IDSAlert type="error"><p>{completeOrderError}</p></IDSAlert>
                  </div>
                )}
              </form>
            </IDSCard>
          )}
        </section>
      )}
      </div>
    </div>
  );
};

export default ReviewerReview;
